'use strict';

var dialog = require('./dialog');

/* -------------------------
 * LOGIN MODAL
 * -------------------------
 */

/**
 * Appends a query param to a url exactly once, even if this same url/element gets reused across multiple
 * dialog opens (the hidden dialog markup is moved in and out of the dialog container rather than re-fetched
 * each time - see show() below - so naively appending on every open would duplicate the param).
 * @param {string} url - .
 * @param {string} param - e.g. 'loginDialog=true'.
 * @returns {string} - .
 */
function ensureQueryParam(url, param) {
    url = url || '';
    if (url.indexOf(param) >= 0) {
        return url;
    }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + param;
}

/**
 * OwnID's own icon widget (<ownid-button-widget>, the small biometric/device-unlock icon overlaid on the
 * password field) sets an inline margin-left/width on the password input to make room for itself, and
 * recalculates both more than once (initial render, then again e.g. on click). It gets this wrong only in
 * the login dialog, where the content is still display:none (see account/login/logindialogwrapper.isml) at
 * the moment OwnID first measures it - Login-Show/checkout get it right since they're visible from render.
 *
 * Deliberately does NOT touch the widget's own position/display - see _login_pages.scss for why (the dialog
 * renders two of these widgets as a deliberate OwnID crossfade, not a bug). Watches the field's style
 * attribute via MutationObserver and re-applies the correct margin/width every time OwnID rewrites it,
 * using style.setProperty(..., 'important') since OwnID's own values are inline !important and plain
 * .css()/property assignment can't override those.
 *
 * Called from here (dialog) and from login.js's init() (Login-Show/checkout) so all three surfaces share one
 * fix rather than leaving the latter two relying on OwnID's own math indefinitely. login.js calls this with
 * $(document) - .password-holder is also used by unrelated password fields with no OwnID widget at all (e.g.
 * the Reset Password view's new-password/confirm-password fields), so the selector below is scoped to only
 * .password-holder elements that actually contain the widget, rather than every .password-holder input on
 * the page - otherwise this would shrink those unrelated fields to make room for an icon that isn't there.
 * @param {jQuery} $container - Container to search within.
 */
function fixOwnidPasswordFieldSpacing($container) {
    var CORRECT_MARGIN = '71px';
    // Width can't be a fixed constant like margin-left - .password-holder's own width differs by surface -
    // so this reserves the same space relative to whatever the container's width actually is.
    var CORRECT_WIDTH = 'calc(100% - ' + CORRECT_MARGIN + ')';
    $container.find('.password-holder:has(ownid-button-widget) input').each(function () {
        var field = this;
        var applyCorrectSpacing = function () {
            if (field.style.marginLeft !== CORRECT_MARGIN) {
                field.style.setProperty('margin-left', CORRECT_MARGIN, 'important');
            }
            if (field.style.width !== CORRECT_WIDTH) {
                field.style.setProperty('width', CORRECT_WIDTH, 'important');
            }
        };
        applyCorrectSpacing();
        // Dialog content is moved (not cloned) in and out on every open/close, so the same field persists
        // across opens - guard against attaching a duplicate observer each time this runs.
        if (!field.ownidSpacingObserverAttached) {
            field.ownidSpacingObserverAttached = true;
            new MutationObserver(applyCorrectSpacing).observe(field, { attributes: true, attributeFilter: ['style'] });
        }
    });
}

var loginDialog = {
    /**
     * @function
     * @description Shows the account-login form in a dialog
     */
    show: function(callback, controller, action, productID, params, completeUrl) {
        // Most pages pre-render the dialog's full markup, hidden, directly into the page from load time
        // (see account/login/logindialogwrapper, included from the footer) so OwnID's SDK can discover its
        // OTP button through its own one-time page-load scan, the same way it already does on the standalone
        // Login-Show page. This moves (not clones) that existing node into the dialog - cloning would create
        // a new DOM node with none of OwnID's event bindings attached to the original at page load.
        var $formsContainer = $('.login-dialog-forms-container');

        if ($formsContainer.length) {
            $formsContainer.attr('data-controller', controller).attr('data-action', action).attr('data-product', productID);
            $formsContainer.find('form').each(function(i, form) {
                var $form = $(form);
                $form.attr('action', ensureQueryParam($form.attr('action'), 'loginDialog=true'));
            });
            $formsContainer.find('.login-dialog-validate-customer').attr('data-next-controller-url', completeUrl);
            var $passwordReset = $formsContainer.find('#password-reset-button');
            if ($passwordReset.length) {
                $passwordReset.attr('data-target', ensureQueryParam($passwordReset.attr('data-target'), 'loginDialog=true'));
            }

            var openHiddenDialogContent = function() {
                dialog.open({
                    target: '#dialog-container',
                    html: $formsContainer,
                    options: {
                        dialogClass: 'login-dialog',
                        draggable: false,
                        open: function() {
                            $('body').addClass('dialog-open');
                            $(this).scrollTop(0);
                            fixOwnidPasswordFieldSpacing($(this));
                            if (callback) {
                                callback();
                            }
                        },
                        close: function() {
                            $('body').removeClass('dialog-open');
                        }
                    }
                });
            };

            // Lightweight per-open prep (known-customer prefill, password-reset/rewarded-token link
            // handling, nearby stores for the registration form) - this static, pre-rendered markup has no
            // way to know the current page/session's own state on its own. See Login.js's loginDialogPrep().
            $.ajax({
                type: 'GET',
                url: Urls.loginDialog,
                dataType: 'json',
                error: function() {
                    openHiddenDialogContent();
                },
                success: function(prep) {
                    if (prep) {
                        $formsContainer.find('input[name*="_username"]').val(prep.username || '');
                        $formsContainer.find('input[name*="_rememberme"]').prop('checked', !!prep.rememberme);
                        if (prep.token) {
                            var $loginForm = $formsContainer.find('form#dwfrm_login');
                            $loginForm.attr('action', ensureQueryParam(Urls.loginFormToken + '?Token=' + encodeURIComponent(prep.token), 'loginDialog=true'));
                        }
                        // The login and registration forms both ship with an empty CSRF token (see
                        // logininclude.isml/createaccount.isml/rewardsregistrationinclude.isml) precisely so
                        // a valid one is never handed out to a passive page load - fill both in now that the
                        // dialog is actually being opened.
                        if (prep.csrfToken) {
                            $formsContainer.find('.csrf-token-field').val(prep.csrfToken);
                        }
                    }
                    openHiddenDialogContent();
                }
            });
        } else {
            // FALLBACK: this page renders its own visible inline login form with the same ids (cart/
            // checkout - see HasInlineLoginForm in autocheckout.isml/LoginWithButtons.isml), so the
            // pre-rendered hidden copy is intentionally skipped here to avoid duplicate DOM ids. Fetch the
            // dialog's full HTML fresh instead, exactly like every surface used to.
            $.ajax({
                type: 'POST',
                url: Urls.loginDialogFallback,
                data: JSON.stringify({
                    controller: controller,
                    action: action,
                    passedQueryString: params
                }),
                contentType: 'application/json',
                error: function(jqXHR, textStatus, errorThrown) {
                    console.log(errorThrown);
                },
                success: function(response) {
                    if (response) {
                        dialog.open({
                            html: response,
                            options: {
                                dialogClass: 'login-dialog',
                                draggable: false,
                                open: function() {
                                    $('body').addClass('dialog-open');
                                    $(this).scrollTop(0);
                                    $(this).find('.login-dialog-forms-container').attr('data-controller', controller).attr('data-action', action).attr('data-product', productID);
                                    $(this).find('.login-dialog-forms-container form').each(function(i, form) {
                                        var actionUrl = $(form).attr('action') + '?loginDialog=true';
                                        $(form).attr('action', actionUrl);
                                    });
                                    $(this).find('.login-dialog-validate-customer').attr('data-next-controller-url', completeUrl);
                                    var $passwordReset = $(this).find('.login-dialog-forms-container #password-reset-button');
                                    $passwordReset.attr('data-target', $passwordReset.attr('data-target') + '?loginDialog=true');
                                    fixOwnidPasswordFieldSpacing($(this));

                                    // This content was AJAX-fetched, not present at page load, so OwnID's own
                                    // global auto-discovery never saw it - manually initialize it instead
                                    // (see int_ownid's ownid.js's window.ownidInitOtpButton). Everywhere else,
                                    // the button already exists in the page from load time, so global
                                    // auto-discovery handles it cleanly on its own.
                                    if (window.ownidInitOtpButton) {
                                        window.ownidInitOtpButton($(this).find('.login-dialog-forms-container'));
                                    }

                                    if (callback) {
                                        callback();
                                    }
                                },
                                close: function() {
                                    $('body').removeClass('dialog-open');
                                }
                            }
                        });
                    }
                }
            });
        }
    },
    /**
     * @function
     * @description Closes the account-login dialog
     */
    close: function() {
        dialog.close();
    },
    // Exposed so login.js can apply the same fix for Login-Show/checkout.
    fixOwnidPasswordFieldSpacing: fixOwnidPasswordFieldSpacing
}

module.exports = loginDialog;
